/**
 * P6 · SpeedIQ drill recommendation library.
 *
 * Maps flag metrics (from side-view norms, rear-view norms, and Cause
 * Analysis rule IDs) to corrective drill blocks. Each recommendation
 * carries:
 *   · drills[]       — keyed exercise names (UI maps to library media)
 *   · duration_weeks
 *   · phase          — I (tissue/mobility) · II (capacity) · III (movement)
 *   · citation
 *
 * V0 ships 12 entries that cover every metric in SIDE_VIEW_NORMS /
 * REAR_VIEW_NORMS + the 5 Cause Analysis rule pairs. V1 will replace this
 * file-local lookup with an extension of /app/backend/intelligence/rules.yaml
 * so the same authoring surface drives Department Verdict + SpeedIQ.
 */

const SIDE_DRILLS = {
  forward_trunk_angle_deg: {
    title: "Forward-trunk angle outside elite window",
    drills: ["wall-drive-acceleration", "sled-march-30%-bw", "hip-flexor-stretch-60s"],
    duration_weeks: 3, phase: "II",
    citation: "Mann 1985 · Weyand 2000",
  },
  shin_angle_pushoff_deg: {
    title: "Push-off shin angle below elite range",
    drills: ["heel-elevated-split-squat", "calf-isometric-30s", "sled-push-50%-bw"],
    duration_weeks: 3, phase: "II",
    citation: "Morin 2015",
  },
  first_step_projection_deg: {
    title: "Shallow first-step projection",
    drills: ["wall-drill-projection", "falling-start-10y", "cone-mirror-gait"],
    duration_weeks: 2, phase: "III",
    citation: "Plisk 2000",
  },
  front_side_knee_height_pct: {
    title: "Low front-side knee drive",
    drills: ["a-skip-3x20m", "high-knee-march-60s", "rear-foot-elevated-split-squat"],
    duration_weeks: 3, phase: "II",
  },
  overstride_index: {
    title: "Overstride detected — heel braking",
    drills: ["bounding-stride-cue", "cadence-overspeed-treadmill", "ankle-stiffness-pogo"],
    duration_weeks: 3, phase: "III",
    citation: "Heiderscheit 2010",
  },
  vertical_oscillation_proxy_pct: {
    title: "Excessive vertical oscillation",
    drills: ["low-amplitude-bound", "horizontal-jump-progression", "iso-calf-hold"],
    duration_weeks: 2, phase: "II",
  },
  cadence_proxy_hz: {
    title: "Cadence outside elite window",
    drills: ["cadence-cue-metronome-220bpm", "wicket-runs-3x30m"],
    duration_weeks: 2, phase: "III",
  },
  torso_stability_score: {
    title: "Torso stability deficit",
    drills: ["pallof-press-anti-rotation", "plank-row-3x10", "deadbug-3x12"],
    duration_weeks: 4, phase: "I",
    citation: "McGill 2010",
  },
  arm_swing_symmetry_pct: {
    title: "Arm-swing asymmetry",
    drills: ["arm-swing-isolation-drill", "single-arm-band-row", "thoracic-rotation-foam"],
    duration_weeks: 2, phase: "I",
  },
};

const REAR_DRILLS = {
  pelvic_drop_max_deg: {
    title: "Frontal-plane pelvic drop",
    drills: ["lateral-band-march", "single-leg-deadlift-hold", "side-plank-hip-abduction"],
    duration_weeks: 3, phase: "II",
    citation: "Bramah 2018 · Souza 2010",
  },
  knee_tracking_dev_deg: {
    title: "Medial knee deviation (valgus proxy)",
    drills: ["lateral-band-walks", "single-leg-squat-mirror", "hip-abduction-iso-30s"],
    duration_weeks: 3, phase: "II",
    citation: "Powers 2010",
  },
  pronation_asymmetry_pct: {
    title: "Foot-strike pronation asymmetry",
    drills: ["short-foot-doming", "single-leg-balance-eyes-closed-30s"],
    duration_weeks: 2, phase: "I",
  },
  foot_path_dev_in: {
    title: "Heel-recovery foot-path deviation",
    drills: ["wicket-runs-rear-view", "a-skip-cue"],
    duration_weeks: 2, phase: "III",
  },
  step_width_proxy_in: {
    title: "Step-width outside neutral range",
    drills: ["narrow-stance-march", "wide-stance-bounding"],
    duration_weeks: 2, phase: "III",
  },
  trunk_lateral_sway_deg: {
    title: "Lateral trunk sway",
    drills: ["loaded-carries-suitcase", "anti-lateral-flexion-band"],
    duration_weeks: 3, phase: "II",
  },
  arm_swing_symmetry_pct: {
    title: "Rear-view arm-swing asymmetry",
    drills: ["arm-swing-isolation-drill", "thoracic-rotation-cable"],
    duration_weeks: 2, phase: "I",
  },
  lower_body_symmetry_pct: {
    title: "L vs R stride mechanics asymmetry",
    drills: ["single-leg-deadlift-3x6", "single-leg-rdl-iso-30s"],
    duration_weeks: 3, phase: "II",
  },
  crossover_detected: {
    title: "Crossover gait detected",
    drills: ["wall-drill-push-mechanic", "cone-mirror-gait", "narrow-stance-march"],
    duration_weeks: 2, phase: "III",
    citation: "Mann 1985",
  },
};

// Cause-Analysis-rule level prescriptions (more holistic than per-metric drills)
const CAUSE_PROTOCOLS = {
  gird_limits_arm_swing: {
    title: "GIRD → arm-swing asymmetry protocol",
    drills: ["sleeper-stretch-60s", "cross-body-adduction-60s", "pec-minor-mfr-60s"],
    duration_weeks: 2, phase: "I",
    citation: "",
  },
  kinetic_chain_pelvic_drop: {
    title: "Kinetic-chain pelvic drop protocol",
    drills: ["lateral-band-march", "single-leg-deadlift-hold", "copenhagen-adductor"],
    duration_weeks: 3, phase: "II",
    citation: "Bramah 2018",
  },
  low_fms_base_rear_flags: {
    title: "Movement-base reset (sub-elite FMS + rear flags)",
    drills: ["deep-squat-corrective", "hurdle-step-cue", "aslr-band"],
    duration_weeks: 3, phase: "I",
    citation: "",
  },
  crossover_first_step: {
    title: "Crossover + shallow first-step block",
    drills: ["wall-drill-push-mechanic", "cone-mirror-gait", "falling-start-10y"],
    duration_weeks: 2, phase: "III",
  },
  head_drift_arm_swing: {
    title: "Head-drift + arm-swing coupling",
    drills: ["wallaceburg-drill", "scap-protraction-retraction", "neck-iso-flexion-30s"],
    duration_weeks: 2, phase: "I",
    citation: "",
  },
};


/** Build a sorted list of recommendations from /analyze response. */
export function buildRecommendations(analyzeResponse) {
  const recs = [];
  const sideFlags = analyzeResponse?.side?.flags || [];
  const rearFlags = analyzeResponse?.rear?.flags || [];
  const causeRules = analyzeResponse?.scores?.cause?.rules || [];

  for (const f of sideFlags) {
    const entry = SIDE_DRILLS[f.metric];
    if (entry) recs.push({ ...entry, source: "side_flag", metric: f.metric, value: f.value, band: f.band });
  }
  for (const f of rearFlags) {
    const entry = REAR_DRILLS[f.metric];
    if (entry) recs.push({ ...entry, source: "rear_flag", metric: f.metric, value: f.value, band: f.band });
  }
  for (const r of causeRules) {
    const entry = CAUSE_PROTOCOLS[r.rule];
    if (entry) recs.push({ ...entry, source: "cause_rule", rule: r.rule, narrative: r.narrative, next_step: r.next_step });
  }
  // Sort by phase (I → II → III), then by source priority (cause > rear > side)
  const phaseOrder = { I: 0, II: 1, III: 2 };
  const sourceOrder = { cause_rule: 0, rear_flag: 1, side_flag: 2 };
  recs.sort((a, b) => (phaseOrder[a.phase] - phaseOrder[b.phase]) || (sourceOrder[a.source] - sourceOrder[b.source]));
  return recs;
}
